import React from "react";
import {
  Button,
  Tag,
  Space,
  Tooltip,
  Popover,
  Table,
  Modal,
  message,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  FileTextOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { formatCurrencyWithConversion, getGlobalDisplayCurrency } from "../../utils/currencyUtils";

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Unified Transactions Columns - All types in one table with type-specific actions
export const getUnifiedTransactionsColumns = (context = {}) => [
  {
    headerName: "Type",
    field: "type",
    width: 150,
    pinned: "left",
    editable: false,
    cellRenderer: (params) => {
      const type = params.value;
      let color = "default";

      switch (type) {
        case "Bank Statement":
          color = "blue";
          break;
        case "Statement History":
          color = "purple";
          break;
        case "Overpayment":
          color = "orange";
          break;
        case "Cash":
          color = "green";
          break;
        case "Credit":
          color = "cyan";
          break;
        case "Partner Transaction":
          color = "magenta";
          break;
        default:
          color = "default";
      }

      return <Tag color={color}>{type}</Tag>;
    },
  },
  {
    headerName: "Number",
    field: "number",
    width: 150,
    editable: false,
  },
  {
    headerName: "Date",
    field: "date",
    width: 150,
    editable: false,
    sortable: true,
    filter: "agDateColumnFilter",
    // CRITICAL: Use comparator for proper chronological sorting
    // The 'date' field contains formatted strings like "Dec 23, 2025"
    // We need to parse and compare actual dates
    comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
      // Use rawDate field if available (ISO format), otherwise parse the formatted date
      const getDateValue = (node, displayValue) => {
        // First try to use the rawDate field (ISO format)
        if (node?.data?.rawDate) {
          return new Date(node.data.rawDate).getTime();
        }
        // Fallback: parse the display value
        if (!displayValue) return 0;
        const parsed = new Date(displayValue);
        return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
      };
      
      const dateA = getDateValue(nodeA, valueA);
      const dateB = getDateValue(nodeB, valueB);
      
      return dateA - dateB;
    },
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
        // Parse the date string from the cell
        const dateParts = cellValue.split('/');
        let cellDate;
        
        if (dateParts.length === 3) {
          // Handle MM/DD/YYYY or YYYY-MM-DD format
          if (cellValue.includes('-')) {
            cellDate = new Date(cellValue);
          } else {
            cellDate = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);
          }
        } else {
          cellDate = new Date(cellValue);
        }
        
        if (isNaN(cellDate.getTime())) return -1;
        
        // Set to midnight for comparison
        cellDate.setHours(0, 0, 0, 0);
        
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
      browserDatePicker: true,
      inRangeFloatingFilterDateFormat: 'YYYY-MM-DD',
    },
  },
  {
    headerName: "Partner",
    field: "partner",
    width: 200,
    editable: false,
    cellRenderer: (params) => {
      const name = params.value || "-";
      const partnerId = params.data?.partnerId;
      const onViewPartner = params.context?.onViewPartner;
      if (partnerId && onViewPartner) {
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewPartner(partnerId);
            }}
            style={{ color: "#1890ff", cursor: "pointer" }}
            title="View partner transactions"
          >
            {name}
          </a>
        );
      }
      return name;
    },
  },
  {
    headerName: "Bank",
    field: "bank",
    width: 150,
    editable: false,
  },
  {
    headerName: "Description",
    field: "description",
    minWidth: 300,
    flex: 2,
    editable: false,
    cellStyle: { whiteSpace: "normal", lineHeight: "1.4" },
    tooltipField: "description",
  },
  {
    headerName: "Balance",
    field: "balance",
    width: 130,
    cellRenderer: (params) => {
      if (params.value === null || params.value === undefined || params.value === "") {
        return <span style={{ color: "#999" }}>-</span>;
      }
      const num = parseFloat(params.value);
      if (Number.isNaN(num)) return <span style={{ color: "#999" }}>-</span>;
      const dp = getDecimalPrecision();
      const { currency: displayCurrency, symbol: displaySymbol, rate } = getGlobalDisplayCurrency();

      if (displayCurrency !== 'USD' && rate && rate > 0) {
        const converted = num * rate;
        return (
          <Tooltip title={`Original: $${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {displaySymbol} {converted.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}
              <SwapOutlined style={{ color: '#1890ff', fontSize: '10px' }} />
            </span>
          </Tooltip>
        );
      }
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
    },
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Detail",
    field: "detail",
    width: 120,
    editable: false,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 120,
    cellRenderer: (params) => {
      if (params.value == null) return `$${(0).toFixed(getDecimalPrecision())}`;
      const num = parseFloat(params.value);
      const dp = getDecimalPrecision();
      const { currency: displayCurrency, symbol: displaySymbol, rate } = getGlobalDisplayCurrency();
      
      if (displayCurrency !== 'USD' && rate && rate > 0) {
        const converted = num * rate;
        return (
          <Tooltip title={`Original: $${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {displaySymbol} {converted.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}
              <SwapOutlined style={{ color: '#1890ff', fontSize: '10px' }} />
            </span>
          </Tooltip>
        );
      }
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
    },
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Allocated",
    field: "allocatedAmount",
    width: 120,
    cellRenderer: (params) => {
      if (params.value == null) return `$${(0).toFixed(getDecimalPrecision())}`;
      const num = parseFloat(params.value);
      const dp = getDecimalPrecision();
      const { currency: displayCurrency, symbol: displaySymbol, rate } = getGlobalDisplayCurrency();
      
      if (displayCurrency !== 'USD' && rate && rate > 0) {
        const converted = num * rate;
        return (
          <Tooltip title={`Original: $${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {displaySymbol} {converted.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}
              <SwapOutlined style={{ color: '#1890ff', fontSize: '10px' }} />
            </span>
          </Tooltip>
        );
      }
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
    },
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Remaining",
    field: "remainingAmount",
    width: 120,
    cellRenderer: (params) => {
      if (params.value == null) return `$${(0).toFixed(getDecimalPrecision())}`;
      const num = parseFloat(params.value);
      const dp = getDecimalPrecision();
      const { currency: displayCurrency, symbol: displaySymbol, rate } = getGlobalDisplayCurrency();
      
      if (displayCurrency !== 'USD' && rate && rate > 0) {
        const converted = num * rate;
        return (
          <Tooltip title={`Original: $${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {displaySymbol} {converted.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}
              <SwapOutlined style={{ color: '#1890ff', fontSize: '10px' }} />
            </span>
          </Tooltip>
        );
      }
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
    },
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Percent Allocated",
    field: "percentAllocated",
    width: 150,
    valueFormatter: (params) => {
      if (params.value == null) return "0%";
      return `${parseFloat(params.value).toFixed(1)}%`;
    },
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Status",
    field: "allocationStatus",
    width: 150,
    cellRenderer: (params) => {
      const status = params.value;
      let color = "default";

      if (status === "Fully Allocated") {
        color = "green";
      } else if (status === "Partially Allocated") {
        color = "orange";
      } else if (status === "Unallocated") {
        color = "red";
      }

      return <Tag color={color}>{status}</Tag>;
    },
    editable: false,
  },
  {
    headerName: "Allocated To",
    field: "invoiceCount",
    width: 200,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains", "notContains", "equals", "notEqual", "startsWith", "endsWith"],
      defaultOption: "contains",
      suppressAndOrCondition: false,
    },
    editable: false,
    valueGetter: (params) => {
      const allocationsRaw = params.data?.allocations || [];
      const allAllocations = Array.isArray(allocationsRaw)
        ? allocationsRaw.filter((a) => a && (
            a.invoice_id || a.invoice ||
            a.target_overpayment_id || a.source_overpayment_id ||
            a.overpayment_id ||
            a.credit_id
          ))
        : [];
      if (allAllocations.length === 0) return "";
      return allAllocations.map((alloc) => {
        if (alloc.invoice_id || alloc.invoice) {
          return alloc?.invoice?.number || alloc?.invoice_number || "Invoice";
        } else if (alloc.overpayment_id) {
          return alloc?.overpayment_number || `OP-${alloc.overpayment_id}`;
        } else if (alloc.target_overpayment_id) {
          return alloc?.overpayment_number || `OP-${alloc.target_overpayment_id}`;
        } else if (alloc.source_overpayment_id) {
          return alloc?.overpayment_number || `OP-${alloc.source_overpayment_id}`;
        } else if (alloc.credit_id) {
          return alloc?.credit_number || `Credit-${alloc.credit_id}`;
        }
        return "";
      }).filter(Boolean).join(", ");
    },
    cellRenderer: (params) => {
      // Get ALL allocations (invoices, overpayments - both outgoing and incoming, credits, created overpayments)
      const allocationsRaw = params.data?.allocations || [];
      const allAllocations = Array.isArray(allocationsRaw)
        ? allocationsRaw.filter((a) => a && (
            a.invoice_id || a.invoice || 
            a.target_overpayment_id || a.source_overpayment_id || // Both directions of OP allocations
            a.overpayment_id || // For transactions that created overpayments
            a.credit_id
          ))
        : [];
      
      // Debug: Log allocations for overpay status transactions
      if (params.data?.allocationStatus === "Overpay") {
        console.warn('[DEBUG AGGRID] Overpay row:', params.data?.id, {
          'allocationsRaw': allocationsRaw,
          'allAllocations': allAllocations,
          'allAllocations.length': allAllocations.length
        });
      }
      
      // Also check if this transaction created an overpayment (for "Overpay" status)
      const createdOverpaymentId = params.data?.overpaymentId;
      const createdOverpaymentNumber = params.data?.overpaymentNumber;
      
      const count = allAllocations.length || params.value || 0;

      if (count === 0)
        return <span style={{ color: "#999" }}>-</span>;

      // Helper to get display text for an allocation
      const getAllocationDisplay = (alloc) => {
        if (alloc.invoice_id || alloc.invoice) {
          // Check if the linked invoice/bill is still in draft status
          const invoiceApproval = alloc?.invoice?.approval_status;
          const allocStatus = alloc?.status;
          const isDraft = allocStatus === 'pending' ||
            invoiceApproval === 'draft' ||
            invoiceApproval === 'pending_management_approval';
          return {
            type: 'invoice',
            label: alloc?.invoice?.number || alloc?.invoice_number || 'Invoice',
            id: alloc?.invoice_id ?? alloc?.invoice?.id ?? alloc?.invoice,
            isDraft,
          };
        } else if (alloc.overpayment_id) {
          // Transaction that created an overpayment (shows OP-XX)
          return {
            type: 'overpayment',
            label: alloc?.overpayment_number || `OP-${alloc.overpayment_id}`,
            id: alloc.overpayment_id
          };
        } else if (alloc.target_overpayment_id) {
          // Outgoing: this OP allocated TO another OP
          return {
            type: 'overpayment',
            label: alloc?.overpayment_number || `OP-${alloc.target_overpayment_id}`,
            id: alloc.target_overpayment_id
          };
        } else if (alloc.source_overpayment_id) {
          // Incoming: another OP allocated TO this OP
          return {
            type: 'overpayment',
            label: alloc?.overpayment_number || `OP-${alloc.source_overpayment_id}`,
            id: alloc.source_overpayment_id
          };
        } else if (alloc.overpayment_number) {
          // Fallback for overpayment_number without specific direction
          return {
            type: 'overpayment',
            label: alloc.overpayment_number,
            id: null
          };
        } else if (alloc.credit_id) {
          return {
            type: 'credit',
            label: alloc?.credit_number || `Credit-${alloc.credit_id}`,
            id: alloc.credit_id
          };
        }
        return { type: 'unknown', label: '-', id: null };
      };

      // Show first allocation + count if multiple
      const firstAllocation = allAllocations[0];
      const firstDisplay = getAllocationDisplay(firstAllocation);

      if (count === 1) {
        const onViewInvoice = params.context?.onViewInvoice;
        
        // Style based on type
        const typeColors = {
          invoice: "#1890ff",
          overpayment: "#fa8c16", // Orange to match overpayment tags
          credit: "#13c2c2",
          unknown: "#999"
        };
        
        if (firstDisplay.type === 'invoice' && firstDisplay.id && onViewInvoice) {
          return (
            <span>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onViewInvoice(firstDisplay.id);
                }}
                style={{ color: typeColors.invoice, cursor: "pointer" }}
              >
                {firstDisplay.label}
              </a>
              {firstDisplay.isDraft && (
                <Tag color="orange" style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Draft</Tag>
              )}
            </span>
          );
        }
        
        return (
          <span>
            <span style={{ color: typeColors[firstDisplay.type] || "#999" }}>
              {firstDisplay.label}
            </span>
            {firstDisplay.isDraft && (
              <Tag color="orange" style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Draft</Tag>
            )}
          </span>
        );
      }

      // Create a table of all allocations for popover
      const onViewInvoice = params.context?.onViewInvoice;
      
      // Type colors for visual distinction
      const typeColors = {
        invoice: "#1890ff",
        overpayment: "#fa8c16", // Orange to match overpayment tags
        credit: "#13c2c2",
        unknown: "#999"
      };
      
      const content = (
        <div style={{ width: 480, maxHeight: 300, overflow: "auto" }}>
          <Table
            dataSource={allAllocations}
            columns={[
              {
                title: "Type",
                dataIndex: "allocation_type",
                key: "type",
                width: 90,
                render: (_, allocation) => {
                  const display = getAllocationDisplay(allocation);
                  const typeLabels = {
                    invoice: "Invoice",
                    overpayment: "Overpayment",
                    credit: "Credit",
                    unknown: "-"
                  };
                  return (
                    <span style={{ 
                      color: typeColors[display.type],
                      fontWeight: 500
                    }}>
                      {typeLabels[display.type]}
                    </span>
                  );
                },
              },
              {
                title: "Reference",
                dataIndex: "invoice_number",
                key: "reference",
                width: 140,
                render: (_, allocation) => {
                  const display = getAllocationDisplay(allocation);
                  
                  if (display.type === 'invoice' && display.id && onViewInvoice) {
                    return (
                      <span>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            onViewInvoice(display.id);
                          }}
                          style={{ color: typeColors.invoice, cursor: "pointer" }}
                        >
                          {display.label}
                        </a>
                        {display.isDraft && (
                          <Tag color="orange" style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Draft</Tag>
                        )}
                      </span>
                    );
                  }
                  
                  return (
                    <span>
                      <span style={{ color: typeColors[display.type] }}>
                        {display.label}
                      </span>
                      {display.isDraft && (
                        <Tag color="orange" style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Draft</Tag>
                      )}
                    </span>
                  );
                },
              },
              {
                title: "Amount",
                dataIndex: "amount",
                key: "amount",
                width: 100,
                render: (amount) => `$${parseFloat(amount || 0).toFixed(2)}`,
              },
              {
                title: "Date",
                dataIndex: "allocation_date",
                key: "date",
                width: 100,
                render: (date, allocation) => {
                  const displayDate = date || allocation?.created_at;
                  return displayDate
                    ? new Date(displayDate).toLocaleDateString()
                    : "-";
                },
              },
            ]}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </div>
      );

      return (
        <Popover
          content={content}
          title={`${count} Allocation${count > 1 ? "s" : ""}`}
          trigger="click"
          placement="left"
        >
          <Button size="small" type="link">
            {firstDisplay.label} +{count - 1}
          </Button>
        </Popover>
      );
    },
  },
  {
    headerName: "Notes",
    field: "notes",
    minWidth: 250,
    flex: 2,
    editable: true,
    cellStyle: { whiteSpace: "normal", lineHeight: "1.4" },
    tooltipField: "notes",
    valueGetter: (params) => {
      return (
        params.data?.notes ||
        params.data?.description ||
        (Array.isArray(params.data?.allocations) &&
        params.data.allocations.length > 0 &&
        params.data.allocations[0]?.description
          ? params.data.allocations[0].description
          : "") || ""
      );
    },
  },
  {
    headerName: "Actions",
    field: "actions",
    width: 200,
    cellRenderer: (params) => {
      const { type, originalData } = params.data;
      const {
        onAffectPayment,
        onAffectOverpayment,
        onAffectCredit,
        onAffectCash,
        onEditStatement,
      } = params.context || {};

      // Bank Statement actions: Pay, O-Pay, Edit, Delete
      if (type === "Bank Statement") {
        const { onEditTransaction, onDeleteTransaction } = params.context || {};
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() =>
                onAffectPayment && onAffectPayment(params.data.originalId)
              }
            >
              Pay
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() =>
                onAffectOverpayment &&
                onAffectOverpayment(params.data.originalId, params.data)
              }
            >
              O-Pay
            </Button>
            <Tooltip title="Edit Transaction">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  onEditTransaction && onEditTransaction(params.data)
                }
              />
            </Tooltip>
            <Tooltip
              title={
                params.data?.allocatedAmount > 0 ||
                params.data?.allocationStatus === "Overpaid" ||
                (Array.isArray(params.data?.allocations) &&
                  params.data.allocations.length > 0)
                  ? "Open allocations to remove them before deleting"
                  : "Delete Transaction"
              }
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => {
                  const hasAllocations =
                    (params.data?.allocatedAmount || 0) > 0 ||
                    params.data?.allocationStatus === "Overpaid" ||
                    (Array.isArray(params.data?.allocations) &&
                      params.data.allocations.length > 0);
                  if (hasAllocations) {
                    // Route to allocation modal for this bank transaction
                    onAffectPayment && onAffectPayment(params.data.originalId);
                  } else {
                    onDeleteTransaction && onDeleteTransaction(params.data);
                  }
                }}
              />
            </Tooltip>
          </Space>
        );
      }

      // Credit actions: Allocate, Edit, Delete
      if (type === "Credit") {
        const { onAffectCredit, onEditCredit, onDeleteCredit } =
          params.context || {};
        const hasAllocations =
          (params.data?.allocatedAmount || 0) > 0 ||
          (Array.isArray(params.data?.allocations) &&
            params.data.allocations.length > 0) ||
          params.data?.allocationStatus === "Fully Allocated" ||
          params.data?.allocationStatus === "Partially Allocated";

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<DollarOutlined />}
              onClick={() =>
                onAffectCredit && onAffectCredit(params.data.originalId)
              }
            >
              Allocate Credit
            </Button>
            <Tooltip title="Edit Credit Note">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEditCredit && onEditCredit(params.data)}
              />
            </Tooltip>
            <Tooltip
              title={
                hasAllocations
                  ? "Open allocations to remove them before deleting"
                  : "Delete Credit Note"
              }
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => {
                  if (!onDeleteCredit) {
                    console.error(
                      "onDeleteCredit handler is not defined in context!"
                    );
                    message.error("Delete handler not available");
                    return;
                  }
                  // Always call the handler - it will check allocations and show appropriate modal
                  onDeleteCredit(params.data);
                }}
              />
            </Tooltip>
          </Space>
        );
      }

      // Overpayment actions: Allocate Overpayment
      if (type === "Overpayment") {
        return (
          <Space>
            <Tooltip
              title={
                params.data?.allocatedAmount > 0
                  ? "Open allocations to remove them before deleting"
                  : "Delete"
              }
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => {
                  const hasAlloc =
                    Math.abs(parseFloat(params.data?.allocatedAmount || 0)) >
                    0.0001;
                  if (hasAlloc) {
                    // Go straight to allocation modal to let the user remove allocations
                    if (context?.onAffectOverpayment) {
                      context.onAffectOverpayment(params.data.originalId);
                    }
                  } else {
                    if (context?.onDeleteOverpayment) {
                      context.onDeleteOverpayment(params.data);
                    }
                  }
                }}
              />
            </Tooltip>
            <Button
              type="primary"
              size="small"
              icon={<DollarOutlined />}
              onClick={() =>
                onAffectOverpayment &&
                onAffectOverpayment(params.data.originalId)
              }
            >
              Allocate
            </Button>
          </Space>
        );
      }

      // Cash actions: Allocate, Edit, Delete
      if (type === "Cash") {
        const { onAffectCash, onEditCash, onDeleteCash } = params.context || {};
        const hasAllocations =
          (params.data?.allocatedAmount || 0) > 0 ||
          (Array.isArray(params.data?.allocations) &&
            params.data.allocations.length > 0) ||
          params.data?.allocationStatus === "Fully Allocated" ||
          params.data?.allocationStatus === "Partially Allocated";

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<DollarOutlined />}
              onClick={() =>
                onAffectCash && onAffectCash(params.data.originalId)
              }
            >
              Allocate Cash
            </Button>
            <Tooltip title="Edit Cash Payment">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEditCash && onEditCash(params.data)}
              />
            </Tooltip>
            <Tooltip
              title={
                hasAllocations
                  ? "Open allocations to remove them before deleting"
                  : "Delete Cash Payment"
              }
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => {
                  if (!onDeleteCash) {
                    console.error(
                      "onDeleteCash handler is not defined in context!"
                    );
                    message.error("Delete handler not available");
                    return;
                  }
                  // Always call the handler - it will check allocations and show appropriate modal
                  onDeleteCash(params.data);
                }}
              />
            </Tooltip>
          </Space>
        );
      }

      // Statement History actions: Edit
      if (type === "Statement History") {
        return (
          <Tooltip title="Edit Statement">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() =>
                onEditStatement && onEditStatement(params.data.originalId)
              }
            />
          </Tooltip>
        );
      }

      // Default actions for other types
      return (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              disabled={
                !params.data?.invoice_id &&
                !params.data?.originalData?.invoice_id &&
                !(
                  Array.isArray(params.data?.allocations) &&
                  params.data.allocations.some((alloc) => alloc?.invoice_id)
                )
              }
              onClick={() => {
                const context = params.context;
                if (!context?.onViewInvoice) {
                  return;
                }

                const directInvoiceId =
                  params.data?.invoice_id ||
                  params.data?.originalData?.invoice_id ||
                  params.data?.invoice?.id;

                const allocations = Array.isArray(params.data?.allocations)
                  ? params.data.allocations.filter(
                      (alloc) => alloc && alloc.invoice_id
                    )
                  : [];

                const uniqueInvoiceIds = Array.from(
                  new Set(
                    allocations.map((alloc) => alloc.invoice_id).filter(Boolean)
                  )
                );

                const safeCall = (invoiceId) => {
                  const parsedId =
                    typeof invoiceId === "string"
                      ? parseInt(invoiceId, 10)
                      : invoiceId;
                  if (!parsedId || Number.isNaN(parsedId)) {
                    message.error("Unable to determine invoice to display");
                    return;
                  }
                  context.onViewInvoice(parsedId);
                };

                if (directInvoiceId) {
                  safeCall(directInvoiceId);
                  return;
                }

                if (uniqueInvoiceIds.length === 0) {
                  message.info("No linked invoice available for this record");
                  return;
                }

                if (uniqueInvoiceIds.length === 1) {
                  safeCall(uniqueInvoiceIds[0]);
                  return;
                }

                let modalRef = null;
                modalRef = Modal.info({
                  title: "Select invoice to view",
                  okButtonProps: { style: { display: "none" } },
                  content: (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {uniqueInvoiceIds.map((invoiceId) => {
                        const matchingAllocation = allocations.find(
                          (alloc) => alloc.invoice_id === invoiceId
                        );
                        const label =
                          matchingAllocation?.invoice_number ||
                          `Invoice #${invoiceId}`;
                        return (
                          <Button
                            key={invoiceId}
                            block
                            onClick={() => {
                              modalRef?.destroy();
                              safeCall(invoiceId);
                            }}
                          >
                            {label}
                          </Button>
                        );
                      })}
                    </Space>
                  ),
                });
              }}
            />
          </Tooltip>
        </Space>
      );
    },
    sortable: false,
    filter: false,
    editable: false,
    pinned: "right",
  },
];

// Currency Formatter - uses currency conversion
const CurrencyFormatter = (params) => {
  if (params.value == null) return "$0.00";
  const currency = params.data?.currency || params.data?.originalData?.currency || "$";
  return formatCurrencyWithConversion(params.value || 0, currency);
};

// Date Formatter
const DateFormatter = (params) => {
  if (!params.value) return "N/A";
  return params.value;
};

// Status Cell Renderer
const StatusCellRenderer = (params) => {
  const status = params.value;
  let color = "default";

  if (status === "Fully Allocated") {
    color = "green";
  } else if (status === "Partially Allocated") {
    color = "orange";
  } else if (status === "Unallocated") {
    color = "red";
  }

  return <Tag color={color}>{status}</Tag>;
};

// Actions Cell Renderer for Bank Statements
const BankStatementsActionsRenderer = (params) => {
  const gridContext = params.context || {};

  const handleView = () => {
    gridContext.onViewStatement?.(params.data.id);
  };

  const handleEdit = () => {
    gridContext.onEditStatement?.(params.data.id);
  };

  const handleAllocate = () => {
    gridContext.onEditTransaction?.(params.data);
  };

  return (
    <Space>
      <Tooltip title="View">
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={handleView}
        />
      </Tooltip>
      <Tooltip title="Edit">
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={handleEdit}
        />
      </Tooltip>
      <Tooltip title="Allocate">
        <Button
          type="text"
          size="small"
          icon={<DollarOutlined />}
          onClick={handleAllocate}
        />
      </Tooltip>
    </Space>
  );
};

// Actions Cell Renderer for History
const HistoryActionsRenderer = (params) => {
  const gridContext = params.context || {};
  const handleRollback = () => {
    gridContext.onRollbackStatement?.(params.data.id, params.data.recordDate);
  };

  return (
    <Space>
      <Tooltip title="Rollback">
        <Button type="text" size="small" onClick={handleRollback}>
          Rollback
        </Button>
      </Tooltip>
    </Space>
  );
};

// History Action Type Renderer
const HistoryActionRenderer = (params) => {
  const action = params.value;
  let color = "default";
  let text = action;

  switch (action) {
    case "+":
      color = "green";
      text = "Created";
      break;
    case "-":
      color = "red";
      text = "Deleted";
      break;
    case "~":
      color = "blue";
      text = "Updated";
      break;
    default:
      color = "default";
      text = action;
  }

  return <Tag color={color}>{text}</Tag>;
};

// Changes Cell Renderer
const ChangesRenderer = (params) => {
  const changes = params.value;
  if (
    !changes ||
    (typeof changes === "object" && Object.keys(changes).length === 0)
  ) {
    return <Tag color="gray">No changes</Tag>;
  }

  if (changes.created) {
    return <Tag color="green">Created</Tag>;
  }

  if (changes.deleted) {
    return <Tag color="red">Deleted</Tag>;
  }

  const displayableChanges = Object.entries(changes).filter(
    ([field]) =>
      !["rollback", "rollback_source", "explicit_rollback"].includes(field)
  );

  if (displayableChanges.length === 0) {
    return changes.rollback ? (
      <Tag color="blue">Rolled back</Tag>
    ) : (
      <Tag color="gray">No changes</Tag>
    );
  }

  return (
    <Space direction="vertical" size="small">
      {displayableChanges.slice(0, 3).map(([field, change]) => (
        <Tag key={field} color="blue">
          {field.replace(/_/g, " ")}:{" "}
          {String(change.old || "").substring(0, 10)}... →{" "}
          {String(change.new || "").substring(0, 10)}...
        </Tag>
      ))}
      {displayableChanges.length > 3 && (
        <Tag color="default">+{displayableChanges.length - 3} more</Tag>
      )}
    </Space>
  );
};

// Actions Cell Renderer for Overpayments
const OverpaymentsActionsRenderer = (params) => {
  const gridContext = params.context || {};
  const handleAllocate = () => {
    gridContext.onAllocateOverpayment?.(params.data);
  };

  return (
    <Space>
      <Tooltip title="Allocate">
        <Button
          type="primary"
          size="small"
          icon={<DollarOutlined />}
          onClick={handleAllocate}
        >
          Allocate
        </Button>
      </Tooltip>
    </Space>
  );
};

// Actions Cell Renderer for Cash
const CashActionsRenderer = (params) => {
  const gridContext = params.context || {};
  const handleView = () => {
    gridContext.onViewCash?.(params.data);
  };

  const handleAllocate = () => {
    gridContext.onAllocateCash?.(params.data);
  };

  return (
    <Space>
      <Tooltip title="View">
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={handleView}
        />
      </Tooltip>
      <Tooltip title="Allocate">
        <Button
          type="text"
          size="small"
          icon={<DollarOutlined />}
          onClick={handleAllocate}
        />
      </Tooltip>
    </Space>
  );
};

// Bank Statements Columns - Match exact structure from working BankStatements.jsx
export const getBankStatementsColumns = () => [
  {
    headerName: "#",
    valueGetter: "node.rowIndex + 1",
    width: 50,
    pinned: "left",
    sortable: false,
    filter: false,
    editable: false,
  },
  {
    headerName: "Bank",
    field: "bank.name",
    width: 120,
    editable: false,
  },
  {
    headerName: "Posting Date",
    field: "transaction_date",
    width: 180,
    valueFormatter: (params) => {
      if (!params.value) return "";
      const date = new Date(params.value);
      return date.toLocaleDateString("en-US");
    },
    editable: false,
  },
  {
    headerName: "Description",
    field: "description",
    minWidth: 400,
    flex: 2,
    editable: false,
    cellStyle: { whiteSpace: "normal", lineHeight: "1.4" },
    tooltipField: "description",
  },
  {
    headerName: "Detail",
    field: "detail",
    width: 120,
    editable: false,
  },
  {
    headerName: "Type",
    field: "type",
    width: 150,
    editable: false,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 120,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Allocated",
    field: "allocated_amount",
    width: 120,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Allocation Status",
    field: "allocationStatus",
    width: 160,
    cellRenderer: (params) => {
      const allocationStatus = params.value;
      if (!allocationStatus) return "";

      const { type, label } = allocationStatus;
      let color = "default";

      if (type === "unallocated") {
        color = "red";
      } else if (type === "partially_allocated") {
        color = "orange";
      } else if (type === "overpay") {
        color = "blue";
      } else if (type === "fully_allocated") {
        color = "green";
      }

      return <Tag color={color}>{label}</Tag>;
    },
    editable: false,
  },
  {
    headerName: "Duplicate Status",
    field: "duplicate_status",
    width: 140,
    cellRenderer: (params) => {
      const status = params.value;
      if (!status) return "";

      let color = "default";
      if (status === "DUPLICATE") {
        color = "red";
      } else if (status === "NEW_ENTRY") {
        color = "green";
      } else {
        color = "orange";
      }

      return <Tag color={color}>{status}</Tag>;
    },
    editable: false,
  },
  {
    headerName: "Vendor",
    field: "vendor_name",
    width: 140,
    cellRenderer: (params) => params.value || "-",
    editable: false,
  },
  {
    headerName: "Invoices",
    field: "allocations",
    width: 100,
    cellRenderer: (params) => {
      const allocations = params.value || [];
      if (!allocations.length) return "No invoices";

      const unique = Array.from(
        new Set(allocations.map((i) => i.invoice_number))
      ).length;

      return `${unique} invoice${unique !== 1 ? "s" : ""}`;
    },
    editable: false,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: 150,
    cellRenderer: BankStatementsActionsRenderer,
    sortable: false,
    filter: false,
    editable: false,
    pinned: "right",
  },
];

// Statements History Columns
export const getStatementsHistoryColumns = () => [
  {
    headerName: "Date",
    field: "date",
    width: 180,
    pinned: "left",
    editable: false,
  },
  {
    headerName: "Statement #",
    field: "statementNumber",
    width: 150,
    editable: false,
  },
  {
    headerName: "Bank Name",
    field: "bankName",
    width: 200,
    editable: false,
  },
  {
    headerName: "Action",
    field: "action",
    width: 120,
    cellRenderer: HistoryActionRenderer,
    editable: false,
  },
  {
    headerName: "User",
    field: "user",
    width: 150,
    editable: false,
  },
  {
    headerName: "Changes",
    field: "changes",
    width: 300,
    cellRenderer: ChangesRenderer,
    editable: false,
  },
  {
    headerName: "Change Reason",
    field: "changeReason",
    width: 200,
    editable: false,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: 120,
    cellRenderer: HistoryActionsRenderer,
    sortable: false,
    filter: false,
    editable: false,
    pinned: "right",
  },
];

// Overpayments Columns
export const getOverpaymentsColumns = () => [
  {
    headerName: "Transaction #",
    field: "transactionNumber",
    width: 150,
    pinned: "left",
    editable: false,
  },
  {
    headerName: "Partner",
    field: "partner",
    width: 200,
    editable: false,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 120,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Remaining Amount",
    field: "remainingAmount",
    width: 150,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Date",
    field: "date",
    width: 120,
    valueFormatter: DateFormatter,
    editable: false,
  },
  {
    headerName: "Allocation Count",
    field: "allocationCount",
    width: 150,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: 150,
    cellRenderer: OverpaymentsActionsRenderer,
    sortable: false,
    filter: false,
    editable: false,
    pinned: "right",
  },
];

// Cash Columns
export const getCashColumns = () => [
  {
    headerName: "Cash #",
    field: "cashNumber",
    width: 150,
    pinned: "left",
    editable: false,
  },
  {
    headerName: "Date",
    field: "date",
    width: 120,
    valueFormatter: DateFormatter,
    editable: false,
  },
  {
    headerName: "Type",
    field: "type",
    width: 120,
    editable: false,
  },
  {
    headerName: "Partner",
    field: "partner",
    width: 200,
    editable: false,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 120,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Allocated Amount",
    field: "allocatedAmount",
    width: 150,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Percent Allocated",
    field: "percentAllocated",
    width: 150,
    valueFormatter: (params) => {
      if (params.value == null) return "0%";
      return `${parseFloat(params.value).toFixed(1)}%`;
    },
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Status",
    field: "allocationStatus",
    width: 150,
    cellRenderer: (params) => {
      const status = params.value;
      let color = "default";

      if (status === "Fully Allocated") {
        color = "green";
      } else if (status === "Partially Allocated") {
        color = "orange";
      } else if (status === "Unallocated") {
        color = "red";
      }

      return <Tag color={color}>{status}</Tag>;
    },
    editable: false,
  },
  {
    headerName: "Invoice Count",
    field: "invoiceCount",
    width: 130,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: 150,
    cellRenderer: CashActionsRenderer,
    sortable: false,
    filter: false,
    editable: false,
    pinned: "right",
  },
];

// Partner Transactions Columns
export const getPartnerTransactionsColumns = () => [
  {
    headerName: "Transaction #",
    field: "transactionNumber",
    width: 150,
    pinned: "left",
    editable: false,
  },
  {
    headerName: "Partner",
    field: "partner",
    width: 200,
    editable: false,
  },
  {
    headerName: "Date",
    field: "date",
    width: 120,
    valueFormatter: DateFormatter,
    editable: false,
  },
  {
    headerName: "Type",
    field: "type",
    width: 120,
    editable: false,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 120,
    valueFormatter: CurrencyFormatter,
    filter: "agNumberColumnFilter",
    editable: false,
  },
  {
    headerName: "Description",
    field: "description",
    minWidth: 300,
    flex: 2,
    editable: true,
    cellStyle: { whiteSpace: "normal", lineHeight: "1.4" },
    tooltipField: "description",
  },
  {
    headerName: "Status",
    field: "status",
    width: 150,
    cellRenderer: StatusCellRenderer,
    editable: false,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: 150,
    cellRenderer: (params) => (
      <Space>
        <Tooltip title="View">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() =>
              params.context?.onViewInvoice?.(
                params.data?.invoice_id || params.data?.id
              )
            }
          />
        </Tooltip>
      </Space>
    ),
    sortable: false,
    filter: false,
    editable: false,
    pinned: "right",
  },
];
